import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { SheetDescription, SheetTitle } from "../../components/ui/sheet/index.js";
import { cn } from "../../lib/utils.js";
import { WorkspaceInternalSidebarHeader } from "../../components/layout/WorkspaceInternalSidebarHeader.js";
import {
  workspaceInternalNavItemClass,
  workspaceInternalNavLabelClass,
} from "../../components/layout/workspace-internal-sidebar.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";
import { SUPER_ADMIN_SETTINGS_NAV } from "./super-admin-settings-nav.js";

export function SuperAdminSettingsSidebar({
  collapsed,
  onCollapsedChange,
  onNavigate,
  onClose,
  variant = "sidebar",
}: {
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNavigate?: () => void;
  onClose?: () => void;
  variant?: "sidebar" | "drawer";
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="super-admin-settings-sidebar"
      data-collapsed={collapsed && !isDrawer ? "true" : "false"}
    >
      {isDrawer ? (
        <header className={contactSheetHeaderClass}>
          <div className="grid min-w-0 gap-1">
            <SheetTitle className={contactSheetTitleClass}>{t("superAdmin.settings.nav.label")}</SheetTitle>
            <SheetDescription className={contactSheetDescriptionClass}>
              {t("superAdmin.settings.nav.drawerDescription")}
            </SheetDescription>
          </div>
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </header>
      ) : (
        <WorkspaceInternalSidebarHeader
          title={t("superAdmin.settings.nav.label")}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          expandLabel={t("superAdmin.settings.nav.expandSidebar")}
          collapseLabel={t("superAdmin.settings.nav.collapseSidebar")}
          collapseTestId="super-admin-settings-sidebar-collapse"
        />
      )}

      <nav
        className={cn("min-h-0 flex-1 overflow-y-auto px-1.5 py-2", isDrawer && "px-3 py-3")}
        aria-label={t("superAdmin.settings.nav.label")}
        data-testid="super-admin-settings-nav"
      >
        <ul className="m-0 grid list-none gap-0.5 p-0">
          {SUPER_ADMIN_SETTINGS_NAV.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey);
            const link = (
              <NavLink
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  workspaceInternalNavItemClass(isActive, collapsed && !isDrawer)
                }
                aria-label={label}
                data-testid={item.testId}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={16}
                      className={cn("shrink-0", isActive ? "text-accent" : "text-muted")}
                      aria-hidden="true"
                    />
                    <span className={workspaceInternalNavLabelClass(showLabels)}>{label}</span>
                  </>
                )}
              </NavLink>
            );

            return (
              <li key={item.id}>
                {collapsed && !isDrawer ? (
                  <SimpleTooltip content={label}>
                    <div className="w-full">{link}</div>
                  </SimpleTooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
